import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Drawer, IconButton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Layout from "../../components/layout/Layout";
import { api } from "../../api/api";
import { tr } from "../../i18n";
import { isSuperAdmin } from "../../utils/accessControl";
import "./activity.css";

/** Kundaktivitet (super-admin): who uses the CMS, how much, and for what. */
const FEATURE_LABELS = {
  overview: "Översikt",
  screens: "Skärmar",
  playlists: "Spellistor",
  layout: "Layout",
  content: "Innehåll",
  schedule: "Schemaläggning",
  users: "Användare",
  integrations: "Integrationer",
  statistics: "Statistik",
  guide: "Guide",
  present: "Skärmdelning",
  screenshot: "Skärmbild",
};
const ROLE_LABELS = { admin: "Admin", user: "Användare", viewer: "Visning", organization: "Organisationskonto" };
const INACTIVE_DAYS = 14;

function hours(min) {
  if (!min) return "0 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

function ago(iso) {
  if (!iso) return "Aldrig";
  const d = Math.floor((Date.now() - Date.parse(iso)) / 86400000);
  if (d <= 0) return "I dag";
  if (d === 1) return "I går";
  if (d < 30) return `${d} dagar sedan`;
  return new Date(iso).toLocaleDateString("sv-SE");
}

function inactive(iso) {
  return !iso || Date.now() - Date.parse(iso) > INACTIVE_DAYS * 86400000;
}

function FeatureBars({ minutes, writes }) {
  const rows = Object.keys(FEATURE_LABELS)
    .map((k) => ({ k, min: minutes?.[k] || 0, w: writes?.[k] || 0 }))
    .filter((r) => r.min || r.w)
    .sort((a, b) => b.min + b.w * 2 - (a.min + a.w * 2));
  const maxMin = Math.max(1, ...rows.map((r) => r.min));
  if (!rows.length) return <p className="act-muted">Ingen aktivitet i perioden.</p>;
  return (
    <ul className="act-bars">
      {rows.map((r) => (
        <li key={r.k}>
          <span className="act-bar-label">{FEATURE_LABELS[r.k]}</span>
          <span className="act-bar-track"><span className="act-bar-fill" style={{ width: `${(r.min / maxMin) * 100}%` }} /></span>
          <span className="act-bar-value">{hours(r.min)} · {r.w} ändr.</span>
        </li>
      ))}
    </ul>
  );
}

function DayStrip({ daily, days }) {
  const byDay = new Map((daily || []).map((d) => [d.day, d]));
  const cells = [];
  for (let i = days - 1; i >= 0; i--) {
    const day = new Date(Date.now() - i * 86400000).toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
    cells.push({ day, min: byDay.get(day)?.minutes || 0, w: byDay.get(day)?.writes || 0 });
  }
  const max = Math.max(1, ...cells.map((c) => c.min));
  return (
    <div className="act-days" role="img" aria-label="Aktiva minuter per dag">
      {cells.map((c) => (
        <span key={c.day} className="act-day" title={`${c.day}: ${hours(c.min)}, ${c.w} ändringar`}>
          <span className="act-day-fill" style={{ height: `${Math.max(c.min ? 8 : 0, (c.min / max) * 100)}%` }} />
        </span>
      ))}
    </div>
  );
}

function CustomerPanel({ orgId, days, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!orgId) return undefined;
    let active = true;
    setData(null);
    setError("");
    api.get(`/usage/org/${orgId}`, { params: { days } })
      .then((r) => active && setData(r.data))
      .catch(() => active && setError("Kunde inte hämta kunden."));
    return () => { active = false; };
  }, [orgId, days]);

  return (
    <Drawer anchor="right" open={!!orgId} onClose={onClose} PaperProps={{ className: "act-drawer" }}>
      <div className="act-drawer-head">
        <h2>{data?.name || "Kund"}</h2>
        <IconButton onClick={onClose} aria-label="Stäng"><CloseIcon /></IconButton>
      </div>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <div className="act-loading"><CircularProgress size={26} /></div>}
      {data && (
        <div className="act-drawer-body">
          <section>
            <h3>Aktiva minuter per dag, senaste {days} dagarna</h3>
            <DayStrip daily={data.daily} days={days} />
          </section>
          <section>
            <h3>Funktioner</h3>
            <FeatureBars minutes={data.featureMinutes} writes={data.featureWrites} />
          </section>
          <section>
            <h3>Personer</h3>
            <div className="act-table-wrap">
              <table className="act-table">
                <thead>
                  <tr><th>Namn</th><th>Roll</th><th>Tid</th><th>Ändringar</th><th>Inloggningar</th><th>Senast aktiv</th><th>Använder mest</th></tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.userId}>
                      <td><div className="act-name">{u.name || "—"}</div><div className="act-muted act-small">{u.email}</div></td>
                      <td>{ROLE_LABELS[u.role] || u.role}</td>
                      <td className="act-num">{hours(u.minutes)}</td>
                      <td className="act-num">{u.writes}</td>
                      <td className="act-num">{u.logins}</td>
                      <td>{ago(u.lastAt)}</td>
                      <td>{FEATURE_LABELS[u.topFeature] || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h3>Senaste händelser</h3>
            {data.events.length === 0 ? (
              <p className="act-muted">Inga händelser än.</p>
            ) : (
              <ul className="act-events">
                {data.events.map((e, i) => (
                  <li key={i}>
                    <span className="act-event-when">{new Date(e.at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</span>
                    <span><b>{e.userName}</b> {e.label.charAt(0).toLowerCase() + e.label.slice(1)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

export default function CustomerActivity({ user }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openOrg, setOpenOrg] = useState(null);
  const allowed = isSuperAdmin(user);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setData(null);
    setError("");
    api.get("/usage/overview", { params: { days } })
      .then((r) => active && setData(r.data))
      .catch(() => active && setError("Kunde inte hämta kundaktivitet."));
    return () => { active = false; };
  }, [days, allowed]);

  const totals = useMemo(() => {
    const c = data?.customers || [];
    return {
      active: c.filter((x) => x.minutes || x.writes || x.logins).length,
      all: c.length,
      minutes: c.reduce((s, x) => s + x.minutes, 0),
      writes: c.reduce((s, x) => s + x.writes, 0),
      logins: c.reduce((s, x) => s + x.logins, 0),
      quiet: c.filter((x) => inactive(x.lastAt)).length,
    };
  }, [data]);

  return (
    <Layout title={tr("Customer activity")} user={user}>
      <Box className="act-page">
        <header className="act-hero">
          <div>
            <h1>Kundaktivitet</h1>
            <p>Vilka kunder som använder Notiz, hur mycket och till vad. Notiz egna konton räknas inte.</p>
          </div>
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(e, v) => v && setDays(v)} aria-label="Period">
            <ToggleButton value={7}>7 dagar</ToggleButton>
            <ToggleButton value={30}>30 dagar</ToggleButton>
            <ToggleButton value={90}>90 dagar</ToggleButton>
          </ToggleButtonGroup>
        </header>

        {!allowed && <Alert severity="info">Den här sidan är bara för Notiz superadmin.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {allowed && !data && !error && <div className="act-loading"><CircularProgress size={28} /></div>}

        {data && (
          <>
            <section className="act-tiles">
              <div className="act-tile"><span>Aktiva kunder</span><b>{totals.active} / {totals.all}</b></div>
              <div className="act-tile"><span>Tid i Notiz</span><b>{hours(totals.minutes)}</b></div>
              <div className="act-tile"><span>Ändringar</span><b>{totals.writes}</b></div>
              <div className="act-tile"><span>Inloggningar</span><b>{totals.logins}</b></div>
              <div className={`act-tile${totals.quiet ? " act-tile--warn" : ""}`}><span>Inaktiva {INACTIVE_DAYS}+ dagar</span><b>{totals.quiet}</b></div>
            </section>

            <div className="act-table-wrap">
              <table className="act-table act-table--click">
                <thead>
                  <tr><th>Kund</th><th>Aktiva personer</th><th>Tid</th><th>Ändringar</th><th>Inloggningar</th><th>Aktiva dagar</th><th>Använder mest</th><th>Senast aktiv</th></tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <tr key={c.orgId} onClick={() => setOpenOrg(c.orgId)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setOpenOrg(c.orgId)}>
                      <td className="act-name">{c.name || "—"}</td>
                      <td className="act-num">{c.activeUsers}</td>
                      <td className="act-num">{hours(c.minutes)}</td>
                      <td className="act-num">{c.writes}</td>
                      <td className="act-num">{c.logins}</td>
                      <td className="act-num">{c.activeDays}</td>
                      <td>{FEATURE_LABELS[c.topFeature] || "—"}</td>
                      <td>
                        <span className={`act-pill${inactive(c.lastAt) ? " act-pill--warn" : ""}`}>{ago(c.lastAt)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="act-muted act-small">
              Tid räknas bara när någon är aktiv i CMS:et (fliken syns och de har rört musen eller tangentbordet de senaste fem minuterna). Mätningen startade när funktionen installerades.
            </p>
          </>
        )}
        <CustomerPanel orgId={openOrg} days={days} onClose={() => setOpenOrg(null)} />
      </Box>
    </Layout>
  );
}
